import { FormEntry } from '@ynomia/dynamic-form';
import {
  convertLocalizedTimeToTimezoneTime,
  convertTimezoneTimeToLocalizedTime,
} from '../../../utils';

export const DELIMITER = ',';

const checkboxTransformation = {
  in: (value?: boolean) => (value ? '1' : ''),
  out: (value?: string) => value === '1',
};

const picklistTransformation = {
  in: (value?: Array<string> | string) => (
    Array.isArray(value) ? value.join(DELIMITER) : (value || '')
  ),
  out: (value?: string, entrySchema?: FormEntry) => {
    if (entrySchema?.properties?.multi && !!value && value !== '') {
      return value.split(DELIMITER);
    }
    if (!value || value === '') {
      return undefined;
    }
    return value;
  },
};

const segmentedPicklistTransformation = {
  in: (value?: { [columnId: string]: string }) => (
    !value ? '' : JSON.stringify(value)
  ),
  out: (value?: string) => {
    if (!value || value === '') {
      return undefined;
    }
    return JSON.parse(value);
  },
};

const timestampTransformation = {
  in: (value?: Date | string, entrySchema?: FormEntry) => {
    const processDateObject = (date: Date): string => {
      let dateStr: string = date.toISOString();

      // Ensures that previous time selections aren't copied with an old date.
      if (entrySchema?.entryComponent === 'time') {
        const today = new Date();

        if (date) {
          today.setHours(date.getHours());
          today.setMinutes(date.getMinutes());
          today.setSeconds(date.getSeconds());
          today.setMilliseconds(date.getMilliseconds());
        }

        dateStr = today.toISOString();
      }

      // Ensures that the timestamp has been converted to the correct local timestamp
      // (which reflects project time) so that the date picker shows an accurate value.
      const tz = entrySchema?.properties?.timezone;
      if (tz) {
        dateStr = convertTimezoneTimeToLocalizedTime(dateStr, tz).toISOString();
      }

      return dateStr;
    };

    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      const parsedDate = new Date(value);
      if (Number.isNaN(parsedDate.getTime())) {
        return '';
      }
      return processDateObject(parsedDate);
    }
    return processDateObject(value);
  },
  out: (value?: string, entrySchema?: FormEntry) => {
    if (!value || value === '') {
      return undefined;
    }
    // If this time picker has a specific timezone against it, we had to fake the timestamp
    // timestamp to appear correct to a user in the date picker - so we now need to convert
    // that back into the actual timezone they were choosing it for:
    const tz = entrySchema?.properties?.timezone;
    if (tz) {
      return convertLocalizedTimeToTimezoneTime(value, tz);
    }
    return new Date(value);
  },
};

const photoTransformation = {
  in: (value?: string | string[]) => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.join(DELIMITER);
  },
  out: (value?: string, entrySchema?: FormEntry) => {
    if (!value || value === '') {
      return undefined;
    }
    if (entrySchema?.properties?.multi) {
      return value.split(DELIMITER);
    }
    return [value];
  },
};

const numberTransformation = {
  in: (value?: number) => (typeof value === 'number' ? String(value) : ''),
  out: (value?: string) => (
    value !== undefined && !Number.isNaN(parseFloat(value))
      ? parseFloat(value)
      : undefined
  ),
};

export const entryTransformations = {
  number: numberTransformation,
  checkbox: checkboxTransformation,
  date: timestampTransformation,
  time: timestampTransformation,
  picklist: picklistTransformation,
  segmented_picklist: segmentedPicklistTransformation,
  datetime: timestampTransformation,
  photo: photoTransformation,
};
